import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const storedUser = localStorage.getItem('user');
        const hasSession = sessionStorage.getItem('_at') || storedUser;
        
        if (hasSession) {
            try {
                const response = await authApi.getMe();
                setUser(response.data);
                setIsAuthenticated(true);
            } catch {
                sessionStorage.removeItem('_at');
                localStorage.removeItem('user');
                setUser(null);
                setIsAuthenticated(false);
            }
        }
        setLoading(false);
    };

    const login = async (email, password) => {
        const response = await authApi.login({ email, password });
        const { access_token, user: userData } = response.data;
        
        // Store token in sessionStorage (not localStorage) as fallback
        sessionStorage.setItem('_at', access_token);
        // Store non-sensitive user data for UI hydration
        localStorage.setItem('user', JSON.stringify(userData));
        
        setUser(userData);
        setIsAuthenticated(true);
        
        return userData;
    };

    const register = async (data) => {
        const response = await authApi.register(data);
        const { access_token, user: userData } = response.data;
        
        sessionStorage.setItem('_at', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        setUser(userData);
        setIsAuthenticated(true);
        
        return userData;
    };

    const logout = async () => {
        try {
            await authApi.logout();
        } catch {
            // Continue even if API call fails
        }
        sessionStorage.removeItem('_at');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
    };

    const updateUser = (userData) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
    };

    const isAdmin = user?.role === 'admin';
    const isCustomer = user?.role === 'customer';

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            isAuthenticated,
            isAdmin,
            isCustomer,
            login,
            register,
            logout,
            updateUser,
            checkAuth,
        }}>
            {children}
        </AuthContext.Provider>
    );
};
